
import React, { useEffect, useRef } from "react";
import {
  Document,
  Page,
  Text,
  View,
  Image,
  StyleSheet,
  pdf,
} from "@react-pdf/renderer";

import logo from "./janareallogo.jpg";

/* ==========================================================================
   SICSS — STUDENT PERIODIC REPORT
   Light • Navy Blue • Champagne Gold • Professional Academic Design
   A4 Landscape | Two Student Reports Per Page
   ========================================================================== */

const COLORS = {
  navy: "#082B4C",
  navyLight: "#16466D",
  navySoft: "#EAF1F7",
  gold: "#CBA454",
  goldLight: "#F8F1E3",
  goldPale: "#FCF9F2",
  white: "#FFFFFF",
  background: "#F8FAFC",
  border: "#DCE4EC",
  borderDark: "#C6D2DE",
  text: "#26384A",
  muted: "#697B8D",
  green: "#177245",
  greenLight: "#EAF6EF",
  red: "#B42332",
  redLight: "#FDEEEF",
  gray: "#EDF1F5",
};

const styles = StyleSheet.create({
  /* ================================ PAGE ================================ */

  page: {
    paddingTop: 13,
    paddingBottom: 35,
    paddingHorizontal: 17,
    backgroundColor: COLORS.background,
    fontFamily: "Helvetica",
    color: COLORS.text,
  },

  /* ============================ PAGE HEADING ============================ */

  pageHeading: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: COLORS.white,
    border: `1 solid ${COLORS.border}`,
    borderLeft: `4 solid ${COLORS.gold}`,
    borderRadius: 8,
    paddingVertical: 8,
    paddingHorizontal: 12,
    marginBottom: 10,
  },

  pageHeadingLeft: {
    flex: 1,
  },

  pageEyebrow: {
    fontSize: 6.5,
    color: COLORS.gold,
    fontWeight: "bold",
    letterSpacing: 1.5,
    marginBottom: 3,
  },

  pageHeadingTitle: {
    fontSize: 12,
    fontWeight: "bold",
    color: COLORS.navy,
  },

  pageHeadingSubtitle: {
    marginTop: 3,
    fontSize: 7,
    color: COLORS.muted,
  },

  pageMeta: {
    minWidth: 90,
    maxWidth: 135,
    backgroundColor: COLORS.navySoft,
    borderRadius: 5,
    paddingVertical: 6,
    paddingHorizontal: 8,
    alignItems: "center",
  },

  pageMetaLabel: {
    fontSize: 6,
    color: COLORS.muted,
    marginBottom: 3,
    textTransform: "uppercase",
  },

  pageMetaValue: {
    fontSize: 8,
    color: COLORS.navy,
    fontWeight: "bold",
    textAlign: "center",
  },

  reportWrapper: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    width: "100%",
  },

  /* ============================= REPORT CARD ============================= */

  card: {
    width: "49.35%",
    border: `1 solid ${COLORS.border}`,
    borderRadius: 9,
    padding: 8,
    backgroundColor: COLORS.white,
    position: "relative",
    overflow: "hidden",
  },

  cardTopAccent: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: 3,
    backgroundColor: COLORS.gold,
  },

  /* =========================== WATERMARK BUBBLES ========================= */

  watermarkOne: {
    position: "absolute",
    top: 20,
    right: 8,
    width: 72,
    height: 72,
    borderRadius: 36,
    backgroundColor: COLORS.gold,
    opacity: 0.055,
  },

  watermarkTwo: {
    position: "absolute",
    top: 36,
    right: 35,
    width: 55,
    height: 55,
    borderRadius: 28,
    backgroundColor: COLORS.navyLight,
    opacity: 0.055,
  },

  watermarkThree: {
    position: "absolute",
    top: 45,
    right: 17,
    width: 30,
    height: 30,
    borderRadius: 15,
    border: `1.5 solid ${COLORS.gold}`,
    opacity: 0.15,
  },

  /* ================================ HEADER ================================ */

  header: {
    flexDirection: "row",
    alignItems: "center",
    paddingTop: 3,
    paddingBottom: 7,
    marginBottom: 7,
    borderBottom: `1 solid ${COLORS.border}`,
  },

  logoFrame: {
    width: 48,
    height: 48,
    borderRadius: 12,
    backgroundColor: COLORS.white,
    border: `1.5 solid ${COLORS.gold}`,
    padding: 3,
    marginRight: 9,
    alignItems: "center",
    justifyContent: "center",
  },

  logo: {
    width: 39,
    height: 39,
    borderRadius: 8,
    objectFit: "contain",
  },

  schoolInfo: {
    flex: 1,
    paddingRight: 3,
  },

  schoolName: {
    fontSize: 11.2,
    fontWeight: "bold",
    color: COLORS.navy,
    marginBottom: 3,
  },

  schoolAddress: {
    fontSize: 6.5,
    lineHeight: 1.4,
    color: COLORS.muted,
    marginBottom: 1.5,
  },

  schoolReportBadge: {
    alignSelf: "flex-start",
    backgroundColor: COLORS.navy,
    borderRadius: 3,
    paddingVertical: 3,
    paddingHorizontal: 6,
    marginTop: 4,
  },

  reportTitle: {
    color: COLORS.white,
    fontSize: 6.5,
    fontWeight: "bold",
    letterSpacing: 0.7,
  },

  /* ============================ STUDENT DETAILS =========================== */

  sectionHeading: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 5,
  },

  sectionAccent: {
    width: 3,
    height: 10,
    backgroundColor: COLORS.gold,
    borderRadius: 2,
    marginRight: 5,
  },

  sectionHeadingText: {
    fontSize: 7.5,
    color: COLORS.navy,
    fontWeight: "bold",
    letterSpacing: 0.3,
  },

  infoBox: {
    backgroundColor: "#F7F9FC",
    border: `1 solid ${COLORS.border}`,
    borderRadius: 6,
    paddingVertical: 6,
    paddingHorizontal: 7,
    marginBottom: 7,
  },

  infoRow: {
    flexDirection: "row",
    marginBottom: 4,
    alignItems: "flex-start",
  },

  infoRowLast: {
    marginBottom: 0,
  },

  label: {
    width: 65,
    fontSize: 7,
    fontWeight: "bold",
    color: COLORS.muted,
  },

  value: {
    flex: 1,
    fontSize: 7.3,
    color: COLORS.text,
    fontWeight: "bold",
  },

  studentNameValue: {
    color: COLORS.navy,
    fontSize: 8,
  },

  /* ============================= SUBJECT TABLE ============================ */

  table: {
    width: "100%",
    border: `1 solid ${COLORS.border}`,
    borderRadius: 5,
    overflow: "hidden",
    marginTop: 1,
  },

  tableHeader: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: COLORS.navy,
    paddingVertical: 6,
    borderBottom: `2 solid ${COLORS.gold}`,
  },

  headerSubject: {
    width: "74%",
    textAlign: "left",
    fontSize: 7,
    fontWeight: "bold",
    paddingLeft: 7,
    color: COLORS.white,
    letterSpacing: 0.5,
  },

  headerScore: {
    width: "26%",
    textAlign: "center",
    fontSize: 7,
    fontWeight: "bold",
    color: COLORS.white,
    letterSpacing: 0.5,
  },

  tableRow: {
    flexDirection: "row",
    minHeight: 17,
    alignItems: "center",
    borderBottom: `0.6 solid ${COLORS.border}`,
  },

  tableRowAlternate: {
    backgroundColor: "#F7F9FC",
  },

  subjectCell: {
    width: "74%",
    paddingLeft: 7,
    paddingRight: 4,
    paddingVertical: 4,
    fontSize: 7.3,
    color: COLORS.text,
  },

  scoreCell: {
    width: "26%",
    textAlign: "center",
    fontSize: 7.3,
    fontWeight: "bold",
    paddingVertical: 4,
    borderLeft: `0.6 solid ${COLORS.border}`,
  },

  scoreGood: {
    color: COLORS.green,
  },

  scoreBad: {
    color: COLORS.red,
  },

  scoreNeutral: {
    color: COLORS.navy,
  },

  emptySubjects: {
    width: "100%",
    textAlign: "center",
    fontSize: 7.5,
    paddingVertical: 12,
    color: COLORS.muted,
  },

  /* ============================== PERFORMANCE ============================= */

  summaryBox: {
    marginTop: 7,
    padding: 6,
    backgroundColor: COLORS.goldPale,
    border: `1 solid ${COLORS.border}`,
    borderRadius: 6,
  },

  summaryHeading: {
    fontSize: 7,
    fontWeight: "bold",
    color: COLORS.navy,
    marginBottom: 5,
  },

  summaryRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 4,
  },

  summaryRowLast: {
    marginBottom: 0,
  },

  summaryLabel: {
    flex: 1,
    fontSize: 7,
    color: COLORS.muted,
    fontWeight: "bold",
  },

  summaryValue: {
    flex: 1,
    fontSize: 7.5,
    color: COLORS.navy,
    fontWeight: "bold",
    textAlign: "right",
  },

  averagePill: {
    backgroundColor: COLORS.navySoft,
    borderRadius: 4,
    paddingVertical: 3,
    paddingHorizontal: 6,
    alignSelf: "flex-end",
  },

  averageValue: {
    fontSize: 8,
    color: COLORS.navy,
    fontWeight: "bold",
  },

  remarkValue: {
    color: COLORS.green,
  },

  /* ================================ SIGNATURES ============================ */

  signatures: {
    marginTop: 11,
    flexDirection: "row",
    justifyContent: "space-between",
  },

  signatureBox: {
    width: "43%",
    alignItems: "center",
  },

  signatureLine: {
    width: "100%",
    borderTop: `1 solid ${COLORS.borderDark}`,
    marginBottom: 4,
  },

  signatureText: {
    fontSize: 7,
    fontWeight: "bold",
    color: COLORS.navy,
    textAlign: "center",
  },

  signatureSubtext: {
    fontSize: 6,
    color: COLORS.muted,
    textAlign: "center",
    marginTop: 2,
  },

  /* ================================= FOOTER =============================== */

  cardFooter: {
    marginTop: 8,
    paddingTop: 5,
    borderTop: `0.7 solid ${COLORS.border}`,
    alignItems: "center",
  },

  cardFooterText: {
    fontSize: 6,
    color: COLORS.muted,
    textAlign: "center",
  },

  /* ============================ PAGE FOOTER =============================== */

  pageFooter: {
    position: "absolute",
    left: 17,
    right: 17,
    bottom: 12,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    borderTop: `1 solid ${COLORS.border}`,
    paddingTop: 5,
  },

  pageFooterLeft: {
    fontSize: 6.5,
    color: COLORS.muted,
  },

  pageFooterRight: {
    fontSize: 6.5,
    color: COLORS.navy,
    fontWeight: "bold",
  },

  emptyCard: {
    width: "49.35%",
  },
});

/* ==========================================================================
   HELPERS
   ========================================================================== */

const isLowScore = (score) => {
  if (typeof score === "number") {
    return score < 70;
  }

  if (typeof score === "string") {
    const normalized = score.trim().toUpperCase();

    // Preserve support for letter grades.
    if (["D", "F"].includes(normalized)) {
      return true;
    }

    // Also correctly handle formatted numeric strings, e.g. "65" or "65.5".
    const numericScore = Number(normalized);

    if (normalized !== "" && Number.isFinite(numericScore)) {
      return numericScore < 70;
    }
  }

  return false;
};

const getScoreStyle = (score) => {
  if (score === null || score === undefined || score === "") {
    return styles.scoreNeutral;
  }

  return isLowScore(score) ? styles.scoreBad : styles.scoreGood;
};

const groupReports = (reports = []) => {
  const pages = [];

  for (let i = 0; i < reports.length; i += 2) {
    pages.push(reports.slice(i, i + 2));
  }

  return pages;
};

const displayValue = (value, fallback = "N/A") => {
  if (value === null || value === undefined || value === "") {
    return fallback;
  }

  return String(value);
};

/* ==========================================================================
   STUDENT REPORT CARD
   ========================================================================== */

const StudentGradeCard = ({ report }) => {
  const subjects = Array.isArray(report?.student_subjects)
    ? report.student_subjects
    : [];

  if (!report) {
    return <View style={styles.emptyCard} />;
  }

  return (
    <View style={styles.card} wrap={false}>
      {/* Decorative overlapping watermark bubbles */}

      <View style={styles.watermarkOne} />
      <View style={styles.watermarkTwo} />
      <View style={styles.watermarkThree} />

      <View style={styles.cardTopAccent} />

      {/* School branding */}

      <View style={styles.header}>
        <View style={styles.logoFrame}>
          <Image src={logo} style={styles.logo} />
        </View>

        <View style={styles.schoolInfo}>
          <Text style={styles.schoolName}>
            Jana L. Williams Educational Foundation
          </Text>

          <Text style={styles.schoolAddress}>
            New Store Community, Mt. Barclay, Montserrado County, Liberia
          </Text>

          <Text style={styles.schoolAddress}>
            Tel: +231-727-2609 / +231-886-484-807
          </Text>

          <View style={styles.schoolReportBadge}>
            <Text style={styles.reportTitle}>
              STUDENT PERIODIC REPORT
            </Text>
          </View>
        </View>
      </View>

      {/* Student information */}

      <View style={styles.sectionHeading}>
        <View style={styles.sectionAccent} />
        <Text style={styles.sectionHeadingText}>
          STUDENT INFORMATION
        </Text>
      </View>

      <View style={styles.infoBox}>
        <View style={styles.infoRow}>
          <Text style={styles.label}>Student Name</Text>
          <Text style={[styles.value, styles.studentNameValue]}>
            {displayValue(report.student_name)}
          </Text>
        </View>

        <View style={styles.infoRow}>
          <Text style={styles.label}>Grade / Class</Text>
          <Text style={styles.value}>
            {displayValue(report.grade_class_name)}
          </Text>
        </View>

        <View style={styles.infoRow}>
          <Text style={styles.label}>Academic Period</Text>
          <Text style={styles.value}>
            {displayValue(report.period_name)}
          </Text>
        </View>

        {report.academic_year_name && (
          <View style={[styles.infoRow, styles.infoRowLast]}>
            <Text style={styles.label}>Academic Year</Text>
            <Text style={styles.value}>
              {displayValue(report.academic_year_name)}
            </Text>
          </View>
        )}
      </View>

      {/* Subject scores */}

      <View style={styles.sectionHeading}>
        <View style={styles.sectionAccent} />
        <Text style={styles.sectionHeadingText}>
          ACADEMIC PERFORMANCE
        </Text>
      </View>

      <View style={styles.table}>
        <View style={styles.tableHeader}>
          <Text style={styles.headerSubject}>SUBJECTS</Text>
          <Text style={styles.headerScore}>SCORE</Text>
        </View>

        {subjects.length > 0 ? (
          subjects.map((subject, index) => (
            <View
              key={`${subject.subject_name || "subject"}-${index}`}
              style={[
                styles.tableRow,
                index % 2 === 1 && styles.tableRowAlternate,
              ]}
              wrap={false}
            >
              <Text style={styles.subjectCell}>
                {displayValue(subject.subject_name, "Unnamed Subject")}
              </Text>

              <Text
                style={[
                  styles.scoreCell,
                  getScoreStyle(subject.formatted_final_score),
                ]}
              >
                {displayValue(subject.formatted_final_score)}
              </Text>
            </View>
          ))
        ) : (
          <View style={styles.tableRow}>
            <Text style={styles.emptySubjects}>
              No subject records are available for this report.
            </Text>
          </View>
        )}
      </View>

      {/* Performance summary */}

      <View style={styles.summaryBox} wrap={false}>
        <Text style={styles.summaryHeading}>
          PERFORMANCE SUMMARY
        </Text>

        <View style={styles.summaryRow}>
          <Text style={styles.summaryLabel}>Average Score</Text>

          <View style={styles.averagePill}>
            <Text
              style={[
                styles.averageValue,
                getScoreStyle(report.average_score),
              ]}
            >
              {displayValue(report.average_score)}
            </Text>
          </View>
        </View>

        <View style={styles.summaryRow}>
          <Text style={styles.summaryLabel}>Teacher's Remark</Text>

          <Text
            style={[
              styles.summaryValue,
              styles.remarkValue,
            ]}
          >
            {displayValue(report.remark)}
          </Text>
        </View>

        <View style={[styles.summaryRow, styles.summaryRowLast]}>
          <Text style={styles.summaryLabel}>Class Rank</Text>

          <Text style={styles.summaryValue}>
            {displayValue(report.rank)}
          </Text>
        </View>
      </View>

      {/* Approval and signatures */}

      <View style={styles.signatures} wrap={false}>
        <View style={styles.signatureBox}>
          <View style={styles.signatureLine} />

          <Text style={styles.signatureText}>
            Class Teacher
          </Text>

          <Text style={styles.signatureSubtext}>
            Signature / Date
          </Text>
        </View>

        <View style={styles.signatureBox}>
          <View style={styles.signatureLine} />

          <Text style={styles.signatureText}>
            Principal
          </Text>

          <Text style={styles.signatureSubtext}>
            Signature / Date
          </Text>
        </View>
      </View>

      {/* Report card footer */}

      <View style={styles.cardFooter}>
        <Text style={styles.cardFooterText}>
           Generated by Polu Integrated School Management System
        </Text>
      </View>
    </View>
  );
};

/* ==========================================================================
   CLASS REPORT PDF DOCUMENT
   ========================================================================== */

export const ClassReportDocument = ({
  reports = [],
  gradeClass,
  period,
}) => {
  const pages = groupReports(reports);

  return (
    <Document
      title={`SICSS Class ${gradeClass?.name || ""} ${
        period?.name || ""
      } Report`}
      author="Salvation In Christ School System"
      subject="Student Periodic Grade Report"
      creator="Polu Integrated School Management System"
      producer="React PDF"
    >
      {pages.map((pageReports, pageIndex) => (
        <Page
          key={pageIndex}
          size="A4"
          orientation="landscape"
          style={styles.page}
        >
          {/* Page heading */}

  

          {/* Two student reports per landscape page */}

          <View style={styles.reportWrapper}>
            <StudentGradeCard report={pageReports[0]} />

            {pageReports[1] ? (
              <StudentGradeCard report={pageReports[1]} />
            ) : (
              <View style={styles.emptyCard} />
            )}
          </View>

          {/* Fixed page footer */}

          <View fixed style={styles.pageFooter}>
            <Text style={styles.pageFooterLeft}>
              SICSS • Generated by Polu Integrated School Management System
            </Text>

            <Text
              style={styles.pageFooterRight}
              render={({ pageNumber, totalPages }) =>
                `PAGE ${pageNumber} OF ${totalPages}`
              }
            />
          </View>
        </Page>
      ))}
    </Document>
  );
};

/* ==========================================================================
   AUTOMATIC PDF DOWNLOAD
   ========================================================================== */

const ClassReportPDF = ({
  reports,
  gradeClass,
  period,
  onDownloaded,
  onError,
}) => {
  const hasStartedRef = useRef(false);

  useEffect(() => {
    if (!reports?.length) {
      hasStartedRef.current = false;
      return;
    }

    if (hasStartedRef.current) {
      return;
    }

    hasStartedRef.current = true;

    let isCancelled = false;
    let downloadUrl = null;

    const generateAndDownload = async () => {
      try {
        const blob = await pdf(
          <ClassReportDocument
            reports={reports}
            gradeClass={gradeClass}
            period={period}
          />
        ).toBlob();

        if (isCancelled) {
          return;
        }

        downloadUrl = URL.createObjectURL(blob);

        const link = document.createElement("a");

        link.href = downloadUrl;
        link.download = [
          "SICSS_Class",
          gradeClass?.name || "Report",
          period?.name || "Period",
        ]
          .join("_")
          .replace(/[<>:"/\\|?*\x00-\x1F]/g, "-") + ".pdf";

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        onDownloaded?.();
      } catch (error) {
        if (!isCancelled) {
          console.error("PDF generation failed:", error);
          onError?.(error);
        }
      } finally {
        if (downloadUrl) {
          URL.revokeObjectURL(downloadUrl);
        }
      }
    };

    generateAndDownload();

    return () => {
      isCancelled = true;
    };
  }, [
    reports,
    gradeClass,
    period,
    onDownloaded,
    onError,
  ]);

  return null;
};

export default ClassReportPDF;







// import React, { useEffect, useRef } from "react";
// import {
//   Document,
//   Page,
//   Text,
//   View,
//   Image,
//   StyleSheet,
//   pdf,
// } from "@react-pdf/renderer";

// import logo from "./savationChristLogo.png";

// /* -------------------------------------------------------------------------- */
// /*                                  STYLES                                    */
// /* -------------------------------------------------------------------------- */

// const MAROON = "#6e3700";
// const BLUE = "#1d4ed8";
// const LIGHT = "#f8fafc";
// const BORDER = "#cbd5e1";
// const DARK = "#1e293b";
// const GRAY = "#64748b";

// const styles = StyleSheet.create({
// page: {
//     paddingTop: 12,
//     paddingBottom: 16,
//     paddingHorizontal: 12,
//     backgroundColor: "#fff",
//     fontFamily: "Helvetica",
// },

//   pageTitle: {
//     textAlign: "center",
//     fontSize: 18,
//     color: "#111827",
//     fontWeight: "bold",
//     marginBottom: 12,
//   },

//   wrapper: {
//     flexDirection: "row",
//     justifyContent: "space-between",
//     width: "100%",
//   },

// card: {
//   width: "49%",
//   border: "1 solid #d1d5db",
//   borderRadius: 4,
//   padding: 7,
//   backgroundColor: "#fff",
// },

//   /* ----------------------------- HEADER ----------------------------- */

// header: {
//     flexDirection: "row",
//     borderBottom: "1 solid #d1d5db",
//     paddingBottom: 5,
//     marginBottom: 5,
//     alignItems: "center",
// },

//   logo: {
// width:42,
// height:42,
//     marginRight: 10,
//   },

//   schoolInfo: {
//     flex: 1,
//   },

// schoolName: {
//     color: "#111827",
//     fontSize: 15,
//     fontWeight: "bold",
//   },

//   schoolAddress: {
//     fontSize: 8,
//     color: GRAY,
//     marginTop: 1,
//   },

//   reportTitle: {
//     marginTop: 4,
//     fontSize: 10,
//     color: BLUE,
//     fontWeight: "bold",
//   },

//   /* -------------------------- STUDENT INFO -------------------------- */

// infoBox: {
    
//     borderBottom: "2 solid #7f1d1d",
//     backgroundColor: "#fafafa",
//     padding: 5,
//     borderRadius: 3,
//     marginBottom: 5,
// },

//   infoRow: {
//     flexDirection: "row",
//     marginBottom: 3,
//   },

//   label: {
//     width: 72,
//     fontSize: 8,
//     color: DARK,
//     fontWeight: "bold",
//   },

//   value: {
//     flex: 1,
//     fontSize: 8,
//     color: DARK,
//   },

//   /* --------------------------- TABLE --------------------------- */

//   table: {
//     width: "100%",
//     border: "1 solid #cbd5e1",
//     marginTop: 4,
//   },

//   tableHeader: {
//     flexDirection: "row",
//     backgroundColor: "#e2e8f0",
//     borderBottom: "1 solid #cbd5e1",
//     paddingVertical: 3,
//   },

// headerSubject: {
//   width: "75%",
//   textAlign: "left",
//   fontSize: 8,
//   fontWeight: "bold",
//   paddingLeft: 5,
//   color: DARK,
//   borderRight: "1 solid #cbd5e1",
// },

// headerScore: {
//   width: "25%",
//   textAlign: "center",
//   fontSize: 8,
//   fontWeight: "bold",
//   color: DARK,
// },

// tableRow: {
//   flexDirection: "row",
//   borderBottom: "1 solid #e2e8f0",
//   minHeight: 16,
//   alignItems: "center",
// },

// subjectCell: {
//   width: "75%",
//   paddingLeft: 5,
//   fontSize: 8,
//   color: DARK,
//   borderRight: "1 solid #cbd5e1",
//   paddingVertical: 3,
// },

// scoreCell: {
//   width: "25%",
//   textAlign: "center",
//   fontSize: 8,
//   fontWeight: "bold",
//   paddingVertical: 3,
// },

//   scoreGood: {
//     color: BLUE,
//   },

//   scoreBad: {
//     color: "#dc2626",
//   },

//   /* ------------------------- SUMMARY ------------------------- */

// summaryBox: {
//     marginTop:5,
//     padding:4,
//     border:"1 solid #d1d5db",
//     backgroundColor:"#fafafa",
// },

//   summaryRow: {
//     flexDirection: "row",
//     justifyContent: "space-between",
//     marginBottom: 4,
//   },

//   summaryLabel: {
//     fontSize: 8,
//     fontWeight: "bold",
//     color: DARK,
//   },

//   summaryValue: {
//     fontSize: 8,
//     color: BLUE,
//     fontWeight: "bold",
//   },

//   /* ---------------------- SIGNATURES ---------------------- */

//   signatures: {
//     marginTop: 10,
//     flexDirection: "row",
//     justifyContent: "space-between",
//   },

//   signatureBox: {
//     width: "42%",
//     alignItems: "center",
//   },

//   signatureLine: {
//     width: "100%",
//     borderTop: "1 solid #000",
//     marginBottom: 4,
//   },

//   signatureText: {
//     fontSize: 8,
//     color: DARK,
//   },

//   /* ----------------------- FOOTER ----------------------- */

//   footer: {
//     marginTop: 6,
//     borderTop: "1 solid #cbd5e1",
//     paddingTop: 5,
//     textAlign: "center",
//     fontSize: 7,
//     color: "#666",
//   },
// });

// /* -------------------------------------------------------------------------- */
// /*                            HELPER FUNCTIONS                                */
// /* -------------------------------------------------------------------------- */

// const isLowScore = (score) => {
//   if (typeof score === "number") {
//     return score < 70;
//   }

//   if (typeof score === "string") {
//     return ["D", "F"].includes(score.toUpperCase());
//   }

//   return false;
// };

// const getScoreStyle = (score) =>
//   isLowScore(score) ? styles.scoreBad : styles.scoreGood;


// const groupReports = (reports = []) => {
//   const pages = [];

//   for (let i = 0; i < reports.length; i += 2) {
//     pages.push(reports.slice(i, i + 2));
//   }

//   return pages;
// };



// const StudentGradeCard = ({ report }) => {
//   const subjects = report.student_subjects || [];

//   return (
//     <View style={styles.card}>
//       {/* ============================ HEADER ============================ */}

//       <View style={styles.header}>
//         <Image src={logo} style={styles.logo} />

//         <View style={styles.schoolInfo}>
//           <Text style={styles.schoolName}>
//             Salvation In Christ School System
//             {/* Jana L. Williams Educational Foundation */}
            
           
//           </Text>

//           <Text style={styles.schoolAddress}>
//             1st Cowfield Junction, Paynesville City, Monrovia, Liberia
//             {/* Mt. Barclay New Store Community, Monrovia Liberia */}
//              {/* Zazay Community, Paynesville City, Monrovia */}
//             {/* LPMC By Pass Community Ganta city, Nimba county. */}
//           </Text>

//           <Text style={styles.schoolAddress}>
//             {/* Monrovia, Liberia */}
//           </Text>

//           <Text style={styles.schoolAddress}>
//             Tel: +231-727-2609 / +231-886-484-807
//           </Text>

//           <Text style={styles.reportTitle}>
//             STUDENT PERIODIC REPORT
//           </Text>
//         </View>
//       </View>

//       {/* ======================== STUDENT INFORMATION ======================= */}

//       <View style={styles.infoBox}>
//         <View style={styles.infoRow}>
//           <Text style={styles.label}>Student:</Text>
//           <Text style={styles.value}>
//             {report.student_name}
//           </Text>
//         </View>

//         <View style={styles.infoRow}>
//           <Text style={styles.label}>Grade:</Text>
//           <Text style={styles.value}>
//             {report.grade_class_name}
//           </Text>
//         </View>

//         <View style={styles.infoRow}>
//           <Text style={styles.label}>Period:</Text>
//           <Text style={styles.value}>
//             {report.period_name}
//           </Text>
//         </View>

//         {report.academic_year_name && (
//           <View style={styles.infoRow}>
//             <Text style={styles.label}>Year:</Text>
//             <Text style={styles.value}>
//               {report.academic_year_name}
//             </Text>
//           </View>
//         )}
//       </View>

//       {/* ============================ SUBJECT TABLE ============================ */}

//       <View style={styles.table}>
//         <View style={styles.tableHeader}>
//           <Text style={styles.headerSubject}>
//             SUBJECT
//           </Text>

//           <Text style={styles.headerScore}>
//             SCORE
//           </Text>
//         </View>

//         {subjects.length > 0 ? (
//           subjects.map((subject, index) => (
//             <View
//               key={index}
//               style={styles.tableRow}
//             >
//               <Text style={styles.subjectCell}>
//                 {subject.subject_name}
//               </Text>

//               <Text
//                 style={[
//                   styles.scoreCell,
//                   getScoreStyle(subject.formatted_final_score),
//                 ]}
//               >
//                 {subject.formatted_final_score}
//               </Text>
//             </View>
//           ))
//         ) : (
//           <View style={styles.tableRow}>
//             <Text
//               style={{
//                 width: "100%",
//                 textAlign: "center",
//                 fontSize: 8,
//                 paddingVertical: 8,
//               }}
//             >
//               No Subjects Found
//             </Text>
//           </View>
//         )}
//       </View>

//       {/* ============================ SUMMARY ============================ */}

//       <View style={styles.summaryBox}>
//         <View style={styles.summaryRow}>
//           <Text style={styles.summaryLabel}>
//             Average Score
//           </Text>

//           <Text
//             style={[
//               styles.summaryValue,
//               getScoreStyle(report.average_score),
//             ]}
//           >
//             {report.average_score}
//           </Text>
//         </View>

//         <View style={styles.summaryRow}>
//           <Text style={styles.summaryLabel}>
//             Remark
//           </Text>

//           <Text style={styles.summaryValue}>
//             {report.remark}
//           </Text>
//         </View>

//         <View style={styles.summaryRow}>
//           <Text style={styles.summaryLabel}>
//             Rank
//           </Text>

//           <Text style={styles.summaryValue}>
//             {report.rank || "N/A"}
//           </Text>
//         </View>
//       </View>

//       {/* ============================ SIGNATURES ============================ */}

//       <View style={styles.signatures}>
//         <View style={styles.signatureBox}>
//           <View style={styles.signatureLine} />

//           <Text style={styles.signatureText}>
//             Class Teacher
//           </Text>
//         </View>

//         <View style={styles.signatureBox}>
//           <View style={styles.signatureLine} />

//           <Text style={styles.signatureText}>
//             Principal
//           </Text>
//         </View>
//       </View>

//       {/* ============================ FOOTER ============================ */}

//       <View style={styles.footer}>
//         <Text>
//           Generated by Polu Integrated School Management System
//         </Text>
//       </View>
//     </View>
//   );
// };

// /* -------------------------------------------------------------------------- */
// /*                          CLASS REPORT PDF DOCUMENT                          */
// /* -------------------------------------------------------------------------- */

// // const ClassReportDocument = ({
// //   reports = [],
// //   gradeClass,
// //   period,
// // }) => {
//   export const ClassReportDocument = ({
//   reports = [],
//   gradeClass,
//   period,
// }) => {
//   const pages = groupReports(reports);

//   return (
//     <Document
//       title={`Class ${gradeClass?.name || ""} ${period?.name || ""}`}
//       author="Polu Integrated School Management System"
//       subject="Student Periodic Grade Report"
//       creator="Polu Integrated School Management System"
//       producer="React PDF"
//     >
//       {pages.map((pageReports, pageIndex) => (
//         <Page
//           key={pageIndex}
//           size="A4"
//           orientation="landscape"
//           style={styles.page}
//         >
//           {/* ============================ PAGE TITLE ============================ */}

    

//           <Text
//             style={{
//               textAlign: "center",
//               fontSize: 10,
//               marginBottom: 10,
//               color: "#475569",
//             }}
//           >
//             Class: {gradeClass?.name || ""}
//             {"    |    "}
//             Period: {period?.name || ""}
//           </Text>

//           {/* ========================= TWO REPORTS ========================= */}

//           <View
//             style={{
//                 flexDirection: "row",
//                 justifyContent: "space-between",
//                 alignItems: "flex-start",
//             }}
//             >
//             {/* LEFT REPORT */}

//             <StudentGradeCard
//               report={pageReports[0]}
//             />

//             {/* RIGHT REPORT */}

//             {pageReports[1] ? (
//               <StudentGradeCard
//                 report={pageReports[1]}
//               />
//             ) : (
//               <View
//                 style={{
//                   width: "49%",
//                 }}
//               />
//             )}
//           </View>

//           {/* ============================ PAGE FOOTER ============================ */}

//           <View
//             fixed
//             style={{
//               position: "absolute",
//               left: 20,
//               right: 20,
//               bottom: 12,
//               flexDirection: "row",
//               justifyContent: "space-between",
//               borderTop: "1 solid #cbd5e1",
//               paddingTop: 4,
//             }}
//           >
//             <Text
//               style={{
//                 fontSize: 8,
//                 color: "#64748b",
//               }}
//             >
//               Polu Integrated School Management System
//             </Text>

//             <Text
//               style={{
//                 fontSize: 8,
//                 color: "#64748b",
//               }}
//               render={({ pageNumber, totalPages }) =>
//                 `Page ${pageNumber} of ${totalPages}`
//               }
//             />
//           </View>
//         </Page>
//       ))}
//     </Document>
//   );
// };
// /* -------------------------------------------------------------------------- */
// /*                              DOWNLOAD BUTTON                               */
// /* -------------------------------------------------------------------------- */
// const ClassReportPDF = ({
// reports,
// gradeClass,
// period,
// onDownloaded,
// onError,
// }) => {

// const hasStartedRef = useRef(false);

// useEffect(() => {


// if (!reports?.length) {

//   hasStartedRef.current = false;

//   return;

// }

// if (hasStartedRef.current) {

//   return;

// }

// hasStartedRef.current = true;


// const generateAndDownload = async () => {

//   try {

//     const blob = await pdf(
//       <ClassReportDocument
//         reports={reports}
//         gradeClass={gradeClass}
//         period={period}
//       />
//     ).toBlob();


//     const url =
//       URL.createObjectURL(blob);


//     const link =
//       document.createElement("a");


//     link.href = url;


//     link.download =
//       `Class_${gradeClass?.name}_${period?.name}.pdf`;


//     document.body.appendChild(link);


//     link.click();


//     document.body.removeChild(link);


//     setTimeout(() => {

//       URL.revokeObjectURL(url);

//       onDownloaded?.();

//     }, 1000);


//   }
//   catch(error) {

//     console.error(
//       "PDF generation failed:",
//       error
//     );

//     onError?.(error);

//   }

// };


// generateAndDownload();

// }, [
// reports,
// gradeClass,
// period,
// onDownloaded,
// onError,
// ]);

// return null;

// };

  
// export default ClassReportPDF;